// Mint-WebAdmin · Tổng quan: dữ liệu mẫu sinh theo script board (tên trang / tuyến minh hoạ, không phải số thật).

export type RangeKey = '7' | '30' | '90' | 'month';
export type Lang = '' | 'vi' | 'en' | 'ja' | 'ko' | 'zh' | 'th';
export type Tn = 'ok' | 'warn' | 'err' | 'pri' | 'mute';

export const LANGS = ['vi', 'en', 'ja', 'ko', 'zh', 'th'] as const;
export const LANG_K: Record<(typeof LANGS)[number], string> = { vi: 'Tiếng Việt', en: 'English', ja: '日本語', ko: '한국어', zh: '中文', th: 'ไทย' };
export const RANGES: readonly (readonly [RangeKey, string])[] = [['7', '7 ngày'], ['30', '30 ngày'], ['90', '90 ngày'], ['month', 'Tháng này']];
export const TABS = [['overview', 'Tổng quan'], ['traffic', 'Truy cập'], ['landing', 'Landing page'], ['news', 'Tin tức'], ['texts', 'Chữ trên web'], ['banner', 'Banner'], ['settings', 'Cài đặt']] as const;
export type TabKey = (typeof TABS)[number][0];

const RN: Record<RangeKey, number> = { '7': 7, '30': 30, '90': 90, month: 23 };
const LF: Record<string, number> = { '': 1, vi: 0.62, en: 0.14, ja: 0.08, ko: 0.1, zh: 0.03, th: 0.03 };
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const p2 = (n: number) => `0${n}`.slice(-2);
export const nf = (n: number) => Math.round(n || 0).toLocaleString('vi-VN');
export const pf = (n: number, d = 1) => `${Number.isFinite(n) ? n.toFixed(d).replace('.', ',') : '—'}%`;

export interface LandingRow { id: number; title: string; path: string; src: string; vis: number; quotes: number; won: number; published: boolean }
/** landing đang chạy / giá hết hạn của board (7 trang mẫu, lấy 2 trạng thái này) */
const LANDINGS: readonly LandingRow[] = [
  { id: 101, title: 'FCL Sài Gòn → Busan', path: '/vi/landing/fcl-sai-gon-busan', src: 'Google Ads', vis: 1460, quotes: 31, won: 4, published: true },
  { id: 102, title: '호치민 → 부산 FCL', path: '/ko/landing/fcl-sai-gon-busan', src: 'Google Ads', vis: 410, quotes: 9, won: 1, published: true },
  { id: 103, title: 'Hàng lạnh đi Nhật', path: '/vi/landing/hang-lanh-di-nhat', src: 'Google Ads', vis: 905, quotes: 15, won: 3, published: true },
  { id: 106, title: 'FCL Sài Gòn → Los Angeles', path: '/vi/landing/fcl-sai-gon-los-angeles', src: 'Facebook', vis: 286, quotes: 2, won: 0, published: true },
];

const DIMS = {
  source: ['Google tìm kiếm', 'Google Ads', 'Trực tiếp', 'Facebook', 'Zalo OA'],
  lane: ['VNSGN → KRPUS', 'VNSGN → JPTYO', 'VNSGN → THLCH', 'VNSGN → USLAX', 'VNHPH → KRPUS'],
  country: ['Việt Nam', 'Hàn Quốc', 'Nhật Bản', 'Thái Lan', 'Hoa Kỳ'],
  lang: ['vi', 'en', 'ja', 'ko', 'zh'],
} as const;

export interface Kpi { k: string; v: string; d: string; tone: Tn; s: string }
export interface Card { key: string; k: string; h1: string; h2: string; foot: string; rows: { k: string; n: string; q: string; pct: number }[] }
export interface Overview {
  kpis: Kpi[]; empty: boolean; pts: string; bars: { h: number; tip: string }[]; xl: string[];
  funnel: { k: string; n: string; pct: number; c: string; col: string }[]; cards: Card[];
  landings: { id: number; title: string; path: string; src: string; vis: string; q: string; c: string; won: string }[];
}

const seed = (dim: string) => dim.split('').reduce((a, c) => a + c.charCodeAt(0), 0);

export function overview(range: RangeKey, lang: Lang, empty: boolean): Overview {
  const n = RN[range];
  const fct = (LF[lang] ?? 1) * (empty ? 0 : 1);
  const day0 = Date.UTC(2026, 9, 23);
  const daily = Array.from({ length: n }, (_, i) => {
    const d = new Date(day0 - (n - 1 - i) * 86400000);
    const wd = d.getUTCDay();
    const v = Math.max(0, Math.round((180 + 40 * Math.sin(i / 2.3) + i * 0.6 - (wd === 0 || wd === 6 ? 55 : 0)) * fct));
    const q = Math.max(0, Math.round(v / 40) + (i % 4 === 1 && v ? 1 : 0));
    return { lbl: `${p2(d.getUTCDate())}/${MON[d.getUTCMonth()]}`, v, q };
  });
  const sum = (k: 'v' | 'q') => daily.reduce((s, x) => s + x[k], 0);
  const vis = sum('v'), quo = sum('q'), views = Math.round(vis * 2.6), won = Math.round((11 * n / 30) * fct);
  const conv = vis ? (quo * 100) / vis : 0;
  const prev = { vis: Math.round(vis * 0.88), views: Math.round(views * 0.9), quo: Math.max(0, quo - Math.round((14 * n / 30) * fct)), won: Math.round((8 * n / 30) * fct) };
  const prevConv = prev.vis ? (prev.quo * 100) / prev.vis : 0;
  const delta = (a: number, b: number): [string, Tn] => {
    if (!b) return ['—', 'mute'];
    const d = ((a - b) * 100) / b;
    return [`${d >= 0 ? '+' : ''}${d.toFixed(1).replace('.', ',')}%`, d >= 0 ? 'ok' : 'err'];
  };
  const kpiOf = (k: string, v: string, a: number, b: number, s: string): Kpi => {
    const [d, tone] = delta(a, b);
    return { k, v, d, tone, s };
  };
  const kpis = [
    kpiOf('Khách', nf(vis), vis, prev.vis, `kỳ trước ${nf(prev.vis)}`),
    kpiOf('Lượt xem', nf(views), views, prev.views, `kỳ trước ${nf(prev.views)}`),
    kpiOf('Báo giá', nf(quo), quo, prev.quo, 'phiếu gửi từ web'),
    kpiOf('Chuyển đổi', pf(conv, 2), conv, prevConv, 'báo giá / khách'),
    kpiOf('Phiếu SI Won', nf(won), won, prev.won, 'nguồn website'),
  ];
  const maxV = Math.max(1, ...daily.map((d) => d.v)), maxQ = Math.max(1, ...daily.map((d) => d.q));
  const pts = daily.map((d, i) => `${Math.round(((i + 0.5) / n) * 1000)},${Math.round(170 - (d.v / maxV) * 150)}`).join(' ');
  const bars = daily.map((d) => ({ h: Math.round((d.q / maxQ) * 55), tip: `${d.lbl} · ${d.v} khách · ${d.q} báo giá` }));
  const xl = [0, 1, 2, 3, 4, 5].map((j) => daily[Math.min(n - 1, Math.round((j * (n - 1)) / 5))]!.lbl);
  const f0: readonly (readonly [string, number])[] = [['Xem trang', views], ['Tra lịch tàu', Math.round(vis * 0.41)], ['Mở form báo giá', Math.round(vis * 0.09)], ['Gửi yêu cầu', quo], ['Phiếu SI Won', won]];
  const fcols = ['var(--d-blue)', 'var(--pri)', 'var(--link)', 'var(--d-green)', 'var(--ok)'];
  const funnel = f0.map(([k, v], i) => ({ k, n: nf(v), pct: Math.max(2, Math.round((v / Math.max(1, f0[0]![1])) * 100)), c: i ? pf((v * 100) / Math.max(1, f0[i - 1]![1]), 1) : '100%', col: fcols[i]! }));
  const brk = (dim: keyof typeof DIMS) => {
    const sd = seed(dim), top = ((900 + ((sd * 7) % 2100)) * n / 30) * fct, fall = 1.25 + (sd % 5) / 10;
    return DIMS[dim].map((label, i) => {
      const v = Math.round(top / Math.pow(fall, i));
      return { label, vis: v, quotes: Math.max(0, Math.round(v / (32 + ((sd + i * 3) % 17)))) };
    });
  };
  const card = (key: string, dim: 'source' | 'lane' | 'country', k: string, h1: string, h2: string, foot = ''): Card => {
    const rows = brk(dim);
    const mx = Math.max(1, ...rows.map((r) => r.vis));
    return { key, k, h1, h2, foot, rows: rows.map((r) => ({ k: r.label, n: nf(r.vis), q: nf(r.quotes), pct: Math.round((r.vis / mx) * 100) })) };
  };
  const lm = brk('lang'), lmT = Math.max(1, lm.reduce((a, r) => a + r.vis, 0));
  const cards = [
    card('source', 'source', 'Nguồn', 'Nguồn', 'Khách'),
    card('lane', 'lane', 'Tuyến khách tra nhiều', 'POL → POD', 'Lượt tra'),
    card('country', 'country', 'Quốc gia', 'Quốc gia', 'Khách', `Ngôn ngữ: ${lm.slice(0, 4).map((r) => `${r.label} ${Math.round((r.vis * 100) / lmT)}%`).join(' · ')}`),
  ];
  const landings = [...LANDINGS].sort((a, b) => b.vis - a.vis).slice(0, 5).map((p) => ({
    id: p.id, title: p.title, path: p.path, src: p.src, vis: nf(p.vis * fct), q: nf(p.quotes * fct), c: p.vis ? pf((p.quotes * 100) / p.vis, 1) : '—', won: nf(p.won * fct),
  }));
  return { kpis, empty, pts, bars, xl, funnel, cards, landings };
}

export interface Attn { tag: string; k: string; s: string; when: string; tone: Tn; btn: string }
/** cột phải "Cần chú ý" (vai xuất bản; vai sửa thấy nút "Xem" thay "Duyệt") */
export function attention(canPublish: boolean): Attn[] {
  return [
    { tag: 'Giá hết hạn', k: 'FCL Sài Gòn → Los Angeles', s: 'Khối Giá tham khảo hết hạn 10/Oct · web đang ẩn khối giá', when: '13 ngày', tone: 'err', btn: 'Mở trình dựng' },
    { tag: 'Chờ duyệt', k: 'Phụ phí PSS tuyến Mỹ tháng 11', s: 'ANDG gửi lúc 23/Oct/2026 07:40 · bản vi', when: '1 giờ', tone: 'pri', btn: canPublish ? 'Duyệt' : 'Xem' },
    { tag: 'Hẹn giờ', k: 'GRI 01-Nov tuyến Nhật Bản · LCL đi Thái Lan', s: '1 tin + 1 landing đăng lúc 24/Oct/2026 08:00', when: 'mai', tone: 'warn', btn: 'Xem tin' },
    { tag: 'Sắp hết hiệu lực', k: 'Banner bão số 7', s: 'Hết 25/Oct/2026 23:59 · tự tắt', when: '2 ngày', tone: 'mute', btn: 'Mở banner' },
  ];
}

export interface Build { id: string; s: 'ok' | 'err' | 'run'; k: string; by: string; at: string; d: string }
export const BUILDS: readonly Build[] = [
  { id: 'b-318', s: 'ok', k: 'Xuất bản landing fcl-sai-gon-busan', by: 'TUANNT', at: '23/Oct/2026 08:12', d: '48 giây' },
  { id: 'b-317', s: 'ok', k: 'Đăng tin asia-korea-rates-week-42 (en)', by: 'TRANGHDD', at: '22/Oct/2026 16:52', d: '51 giây' },
  { id: 'b-316', s: 'err', k: 'Đăng tin asia-korea-rates-week-42 (en)', by: 'TRANGHDD', at: '22/Oct/2026 16:40', d: 'lỗi' },
  { id: 'b-315', s: 'ok', k: 'Sửa chữ home.hero.title', by: 'DAVID', at: '21/Oct/2026 10:03', d: '46 giây' },
];
